<template>
  <div :class="[getItemTableContainerClass, $style.containsAlerts]">
    <PaymentPromptBanner v-if="inMainPanel" />
    <UserCapBanner
      v-if="inMainPanel"
      :totalCountOfAllItems="totalCountOfAllItems"
    />
    <ItemTable
      ref="itemTable"
      :additionalBulkActions="getAdditionalBulkActions"
      :boundItemsUrl="boundItemsUrl"
      :bulkActionsList="getBulkActionsList"
      :canExport="inMainPanel"
      :canModifyColumns="inMainPanel"
      :class="$style.itemTable"
      :columnOptions="columnOptions"
      :columns="getColumnsData('columns')"
      :defaultColumns="getColumnsData('defaultColumns')"
      :defaultSortField="defaultSortField"
      :details="inMainPanel"
      :handleBulkAction="handleBulkAction"
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleMainAction="() => {}"
      :handleRowClick="handleRowClick"
      :handleUnselectLockedItem="handleUnselectLockedSystemUser"
      :handleUnselectMultipleLockedItems="handleUnselectMultipleLockedSystemUsers"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="!inMainPanel"
      :isAsideOpen="isAsideOpen"
      :isItemLocked="isSystemUserLocked"
      :itemType="itemType"
      :keepFresh="inMainPanel"
      :linkText="systemUserLinkText"
      :mainActionMenuItems="mainActionMenuItems"
      :route="route"
      :selectedModelId="selectedModelId"
      :selectionItemText="systemUserItemText"
      :setGettingStartedTooltipVisibility="setGettingStartedTooltipVisibility"
      :setSelectedModelId="setSelectedModelId"
      :showBound="!inMainPanel"
      :showBoundInitially="inSystemAside()"
      :showBulkDelete="inMainPanel"
      :showMainAction="inMainPanel"
      :showSearchForm="true"
      :useDropDownFilterComponent="useDropDownFilterComponent"
      :useSearchContextFilterComponent="useSearchContextFilterComponent"
      :viewport="viewport"
      @columnsChange="(columnNames) => $emit('columnsChange', columnNames)"
      @revertColumns="() => $emit('revertColumns')"
    >
      <template
        v-if="showEmptyFilteredUserState"
        v-slot:emptyState
      >
        <EmptyFilteredUserState
          :state="getAppliedFilters[0].value"
        />
      </template>
    </ItemTable>
    <UserDisableMFAModal
      ref="disableMFAModal"
      v-model="mfaModal.disableMFAModalVisible"
    />
    <UserEnableMFAModal
      ref="enableMFAModal"
      v-model="mfaModal.enableMFAModalVisible"
    />
    <UserResetMFAModal
      ref="resetMFAModal"
      v-model="mfaModal.resetMFAModalVisible"
    />
    <ProgressModal
      storeModule="ProgressModule"
    />
    <BulkResendActivationEmailModal
      v-model="showBulkResendActivationEmailModal"
      :selectedUsers="selectedActiveUserDetails"
      @continue="sendSelectedUsersEmailContinue"
    />
    <BulkResendPasswordResetEmailModal
      v-model="showBulkResendPasswordResetEmail"
      :selectedUsers="selectedActiveUserDetails"
      @continue="sendSelectedUsersEmailContinue"
    />
    <ManageSystemUserState
      v-model="systemUserStateChange"
      @close="closeModal"
      @save="suspendSelectedUsers"
    />
    <AlertContainer>
      <Alert
        v-if="deletedUsers.failedTasks.length > 0"
        :interactive="true"
        type="error"
        @hidden="handleDeleteAlertHidden('failedTasks')"
      >
        <span>
          {{ deletedUsers.errorMessage }}
        </span>
        <a
          :class="$style.showDeleteFailureDetailsLink"
          href="javascript:void(0)"
          @click.stop="showDeleteFailureDetails"
        >
          show details
        </a>
      </Alert>
      <Alert
        v-if="show403error"
        :delay="7000"
        type="error"
        @hidden="handle403AlertHidden"
      >
        <span>{{ errorMessageFor403 }}</span>
      </Alert>
      <Alert
        v-if="deletedUsers.deletedIds.length > 0"
        :delay="7000"
        type="success"
        @hidden="handleDeleteAlertHidden('deletedIds')"
      >
        <span>
          {{ deletedUsers.successMessage }}
        </span>
      </Alert>
    </AlertContainer>
    <GrowingWithJumpCloudModal
      :location="$options.usersPage"
      :shouldShow.sync="shouldShowPaywall"
    />
    <SeverityModal
      v-if="showDeleteUserModal"
      isMarkdown
      :notificationMessage="deleteUserNotificationMessage"
      :notificationTitle="severityModalData.notificationTitle"
      :numberAffectedProp="selectedItems.length"
      :primaryButtonProps="primaryButtonProps"
      :showModal="showDeleteUserModal"
      :title="severityModalData.title"
      useInputNumber
      variant="sev3"
      @close="handleCancelDeleteUserModal"
      @save="deleteSelectedUsers"
    >
      <template v-slot:subtitle>
        <span>{{ severityModalData.subtitle }}</span>
      </template>
      <template v-slot:body>
        <ParagraphBody v-if="hasManagers">
          <MarkdownBlock
            data-test-id="DeleteConfirmationNumberSeverityModal__manager"
            :mdTemplate="managerText"
          />
        </ParagraphBody>
      </template>

      <template v-slot:learnmore>
        <ParagraphBody>
          <span>For more information, see </span>
          <Link
            :href="severityModalData.kbLink"
            :style="severityModalData.kbLinkStyle"
            target="_blank"
            :text="severityModalData.kbText"
          />
        </ParagraphBody>
      </template>
      <template v-slot:list>
        <ParagraphBody>
          {{ severityModalData.numConfirmText }}
        </ParagraphBody>
      </template>
    </SeverityModal>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  Link,
  MarkdownBlock,
  ParagraphBody,
  SeverityModal,
  Types,
} from '@jumpcloud/ui-components';
import { PERSONIO } from 'client/util/Constants/ApplicationConstants';
import {
  clone,
  get,
  intersection,
  isEmpty,
  map,
  uniqBy,
  xor,
} from 'lodash';
import {
  eventLocations,
  paymentSources,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { featureFlags } from 'client/store/modules/FeatureFlagsModule';
import { getApplicationTemplatesByName } from 'client/applications/utils/ApplicationUtils';
import { mapActions, mapGetters, mapState } from 'vuex';
import { menuTextColor } from 'client/css/colors.css';
import { systemUserMessageText } from 'client/systemusers/constants/SystemUserConstants';
import ActionsDownIcon from 'public/img/icons/actions-down.svg?inline';
import ActiveDirectoryIcon from 'public/img/icons/nav/icon-directories-ad.svg?inline';
import Alert from 'client/components/Alert/Alert.vue';
import AlertContainer from 'client/components/Alert/AlertContainer.vue';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import BambooIcon from 'public/img/icons/bamboo-hr.svg?inline';
import BulkResendActivationEmailModal from 'client/systemusers/BulkResendEmail/BulkResendActivationEmailModal.vue';
import BulkResendPasswordResetEmailModal from 'client/systemusers/BulkResendEmail/BulkResendPasswordResetEmailModal.vue';
import DirectoryApi from 'client/api/v2/DirectoryApi';
import DirectoryUtil from 'client/util/DirectoryUtil';
import EmptyFilteredUserState from 'client/systemusers/SystemUserItemTable/EmptyFilteredUserState.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import GoogleIcon from 'public/img/icons/google-workspace.svg?inline';
import GrowingWithJumpCloudModal from 'client/Payments/newComponents/GrowingWithJumpCloudModal.vue';
import HiBobIcon from 'public/img/icons/icon-directories-bob.svg?inline';
import ImportFromCSVModalHelper from 'client/systemusers/ImportUsers/ImportFromCSVModalHelper';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableIndirectBindingMixin from 'client/components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import ItemType from 'client/util/Constants/ItemType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LdapAsidePanelMixin from 'client/components/ItemTable/mixins/LdapAsidePanelMixin';
import M365Icon from 'public/img/icons/microsoft365.svg?inline';
import ManageSystemUserState from 'client/systemusers/SystemUserItemTable/ManageSystemUserState.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import NamelyIcon from 'public/img/icons/icon-directories-namely.svg?inline';
import OktaIcon from 'public/img/icons/okta.svg?inline';
import OrganizationsApi from 'client/api/v2/OrganizationsApi';
import PaymentHelper from 'client/Payments/PaymentHelper';
import PaymentPromptBanner from 'client/Payments/components/PaymentPromptBanner';
import PencilIcon from 'public/img/icons/pencil.svg?inline';
import PersonioIcon from 'public/img/icons/personio.svg?inline';
import ProgressModal from 'client/components/Modal/ProgressModal.vue';
import RouterService from 'client/util/RouterService';
import SsoIcon from 'public/img/icons/nav/icon-nav-sso.svg?inline';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import SystemUserItemTableColumns from 'client/systemusers/SystemUserItemTable/SystemUserItemTableColumns';
import UnlockUserModal from 'client/components/Modal/UnlockUserModal.vue';
import UnremovableUsersModal from 'client/components/Modal/UnremovableUsersModal.vue';
import UserCapBanner from 'client/Payments/components/UserCapBanner';
import UserDisableMFAModal from 'client/components/Modal/mfaModals/UserDisableMFAModal.vue';
import UserEnableMFAModal from 'client/components/Modal/mfaModals/UserEnableMFAModal.vue';
import UserResetMFAModal from 'client/components/Modal/mfaModals/UserResetMFAModal.vue';
import UserStateMixin from 'client/components/mixins/UserStateMixin';
import Util from 'client/util/Util';
import WorkdayIcon from 'public/img/icons/workdayAvatar.svg?inline';
import directoryRouteMixin from 'client/components/mixins/directoryRouteMixin';
import pluralize from 'pluralize';
import rbacUtil from 'client/util/rbacUtils';
import util, { notify } from 'client/util';

const {
  DirectoryGroupingConsts,
  DirectoryNameConsts,
  DirectoryTypeConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ModuleConsts,
} = Constants;

const {
  usersPage,
} = eventLocations;

const {
  bambooRealTimeImport,
} = featureFlags;

const { error, success } = Constants.default;
const {
  activeDirectory,
  gSuite,
  bob,
  office365,
  okta,
  workday,
} = DirectoryTypeConsts;

const {
  failedActivationEmailResend,
  selectedItemsEmpty,
  usersCouldNotBeSuspended,
} = ModuleConsts.systemUser.errors;

const directoryCollection = 'DirectoryCollection';
const organizationsCollection = 'OrganizationsCollection';
const { PrimaryButtonProps } = Types;
const settingsModule = 'SettingsModule';
const systemUserCollection = 'SystemUserCollection';
const systemUserItem = new ItemType.SystemUserItemType();

const iconProps = {
  fill: menuTextColor,
  height: '18px',
  style: {
    left: '1px',
    marginRight: '10px',
    position: 'relative',
    verticalAlign: '-5%',
  },
  width: '18px',
};

// Enum of app templates, more will be included in the future
const APPS = {
  personio: 0,
  namely: 1,
};

export default {
  name: 'SystemUserItemTable',

  components: {
    Alert,
    AlertContainer,
    BulkResendActivationEmailModal,
    BulkResendPasswordResetEmailModal,
    EmptyFilteredUserState,
    GrowingWithJumpCloudModal,
    ItemTable,
    Link,
    ManageSystemUserState,
    MarkdownBlock,
    ParagraphBody,
    PaymentPromptBanner,
    ProgressModal,
    SeverityModal,
    UserCapBanner,
    UserDisableMFAModal,
    UserEnableMFAModal,
    UserResetMFAModal,
  },

  usersPage,

  mixins: [
    AsidePanelChildComponentPropsMixin,
    ItemTableComputedMixin,
    ItemTableIndirectBindingMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
    LdapAsidePanelMixin,
    UserStateMixin,
    directoryRouteMixin,
  ],

  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    defaultColumns: {
      type: Array,
      default: () => [],
    },
    panelType: {
      default: ItemTableConsts.panelType.mainPanel,
      type: String,
      required: false,
    },
    showMfaStatus: {
      default: true,
      type: Boolean,
    },
    showNewSudoWorkflow: {
      default: false,
      type: Boolean,
    },
    sudoersContextIsWindows: {
      type: Boolean,
      default: false,
      required: false,
    },
    watchSelectedItems: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data() {
    const { mainPanel } = ItemTableConsts.panelType;

    return {
      appTemplates: [],
      errorMessageFor403: rbacUtil.getForbiddenMessage(),
      deletedUsers: {
        deletedIds: [],
        errorMessage: '',
        failedTasks: [],
        successMessage: '',
      },
      groupType: DirectoryGroupingConsts.groupTypes.userGroup,
      itemType: ItemTypeConsts.systemUser,
      managers: [],
      managerText: '',
      memberType: DirectoryGroupingConsts.memberTypes.user,
      menuTextColor,
      mfaModal: {
        disableMFAModalVisible: false,
        enableMFAModalVisible: false,
        resetMFAModalVisible: false,
      },
      numberOfGSuites: 0,
      numberOfOffice365s: 0,
      route: '#/users',
      selectedActiveUserDetails: [],
      severityModalData: {
        body: '',
        kbText: '',
        kbLink: '',
        kbLinkStyle: { display: 'contents' },
        notificationTitle: '',
        notificationMessage: '',
        numConfirmText: '',
        subtitle: '',
        title: '',
      },
      shouldShowPaywall: false,
      show403error: false,
      showBulkResendActivationEmailModal: false,
      showBulkResendPasswordResetEmail: false,
      showDeleteUserModal: false,
      systemUserItemText: {
        label: this.panelType === mainPanel ? 'selected' : 'bound',
        plural: systemUserItem.labelPlural,
        singular: systemUserItem.label,
      },
      systemUserLinkText: systemUserItem.labelPlural,
      systemUserStateChange: {
        showBulkActivateUserModal: false,
        showSuspendModal: false,
        stateChangeAction: '',
      },
    };
  },

  computed: {
    ...mapState(settingsModule, [
      'hasContract',
      'hasCreditCard',
      'isCustomer',
      'maxSystemUsers',
      'orgId',
      'passwordPolicy',
    ]),

    ...mapState(systemUserCollection, {
      modifiedItemCount: state => state.features.modifiedItemCount,
      boundItemsById: state => state.entities.boundItems.byId,
      previouslySelectedItems: state => state.features.previouslySelectedItems.ids,
      unselectedItems: state => state.features.unselectedItems.ids,
    }),

    ...mapGetters(organizationsCollection, {
      getOrgDetails: 'getItemDetails',
    }),

    ...mapGetters(directoryCollection, {
      directories: 'getCollection',
    }),

    ...mapGetters(systemUserCollection, {
      getAppliedFilters: 'getAppliedFilters',
      getDisplayName: 'getDisplayName',
      getFirstAndLastName: 'getFirstAndLastName',
      getSelectedItems: 'getSelectedItems',
      getSudoersToUpdate: 'getSudoersToUpdate',
      isItemPreviouslySelected: 'isItemPreviouslySelected',
      selectedItems: 'getSelectedItems',
      totalCountOfAllItems: 'getTotalCountOfAllItems',
      usersIneligibleForForcePasswordChange: 'getUsersIneligibleForForcePasswordChange',
      usersIneligibleForSuspension: 'getUsersIneligibleForSuspension',
      usersIneligibleForUnlock: 'getUsersIneligibleForUnlock',
    }),

    ...mapGetters(settingsModule, [
      'hasBilling',
    ]),

    ...mapState('FeatureFlagsModule', {
      [bambooRealTimeImport]:
        state => state.betaFeatures[bambooRealTimeImport],
    }),

    // A couple of applications will be injected into the
    // existing menuItems (in between m365 and Okta). This func is to cover for future apps
    // that may arrive.
    appsMenuItems() {
      /**
       * Function to access the APPS enum and return [appName, index]
       */
      function getAppEnumInfo(name) {
        return Object.entries(APPS).find(([key]) => key === name);
      }

      const menuItems = [];

      if (this.appTemplates.length !== 0) {
        let [appName, index] = [null, null];

        [appName, index] = getAppEnumInfo('namely');

        menuItems.push({
          clickHandler: `/#/sso/configure/${this.appTemplates[index][appName]._id}`,
          component: NamelyIcon,
          componentProps: iconProps,
          text: DirectoryUtil.getDirectoryImportMenuItemText('Namely'),
        });

        // Set style
        const personioIconProps = { ...iconProps };
        personioIconProps.style.overflow = 'visible';
        delete personioIconProps.style.left;

        // Set applicaiton template Id to craft redirect URL
        [appName, index] = getAppEnumInfo(PERSONIO.toLowerCase());

        menuItems.push({
          clickHandler: `/#/sso/configure/${this.appTemplates[index][appName]._id}`,
          component: PersonioIcon,
          componentProps: personioIconProps,
          text: DirectoryUtil.getDirectoryImportMenuItemText(PERSONIO),
        });
      }

      return menuItems;
    },

    columnOptions() {
      const requiredFields = this.systemUserRequiredFields;

      return this.usersMainPanelColumnOptions.map(column => ({
        name: column.name,
        value: column.headerName || column.name,
        required: requiredFields.includes(column.name),
      }));
    },

    deleteUserNotificationMessage() {
      return `Deletion will permanently remove all user data and associations. This action cannot be undone.

    • This will result in the removal of all user data and associations, including associations with devices and applications.

    • Users removed from devices will no longer be able to log in to their JumpCloud-managed devices.

    • You are about to permanently delete **${this.selectedItems.length}** user(s).
    `;
    },

    disableGSuiteImport() {
      return this.numberOfGSuites === 0;
    },

    disableOffice365Import() {
      return this.numberOfOffice365s === 0;
    },

    /**
     * This is the `more actions` dropdown list
     */
    getAdditionalBulkActions() {
      const menuItemsArr = [];

      if (this.inMainPanel) {
        menuItemsArr.push(
          {
            label: 'Resend Email',
            action: () => this.sendSelectedUsersEmails(),
          }, {
            label: 'Force Password Change',
            action: () => this.handleBulkForcePasswordChange(),
            addSeparator: true,
          }, {
            label: 'Unlock User Account',
            action: () => this.handleBulkUnlock(),
          }, {
            label: 'Require User MFA',
            action: () => this.handleBulkEnableMFA(),
            addSeparator: true,
          }, {
            label: 'Remove User MFA Requirement',
            action: () => this.handleBulkDisableMFA(),
          }, {
            label: 'Reset TOTP MFA',
            action: () => this.handleBulkResetMFA(),
          },
        );
      }

      return this.inMainPanel ? [{ menuItems: menuItemsArr }] : [];
    },

    /**
     * This is the `change state` dropdown list
     */
    getBulkActionsList() {
      const menuItems = [];

      if (this.inMainPanel) {
        menuItems.push(
          {
            label: 'Activate',
            action: () => this.handleActivateUser(),
          }, {
            label: 'Suspend',
            action: () => this.handleSuspendUser(),
          },
        );
      }

      return this.inMainPanel ? [{ menuItems }] : [];
    },

    hasManagers() {
      return !isEmpty(this.managers);
    },

    isProviderOrg() {
      return !!get(this.getOrgDetails(this.orgId), 'provider', false);
    },

    mainActionMenuItems() {
      const bambooHR = 'BambooHR';
      const gSuiteIntegrationMenuText = DirectoryUtil
        .getDirectoryImportMenuItemText(DirectoryNameConsts[gSuite]);

      const M365IntegrationMenuText = DirectoryUtil
        .getDirectoryImportMenuItemText(DirectoryNameConsts[office365]);

      const mainActionMenuItems = [
        {
          clickHandler: this.handleMainAction,
          component: PencilIcon,
          componentProps: iconProps,
          text: 'Manual user entry',
        },
        {
          clickHandler: this.showImportFromCSVModal,
          component: ActionsDownIcon,
          componentProps: iconProps,
          text: 'Import from CSV',
        },
        {
          clickHandler: () => {
            if (this.numberOfGSuites > 1) {
              Util.redirect(this.directoryTypeRoute(DirectoryTypeConsts.gSuite));
            } else {
              this.navigateToDirectoryAside(gSuite);
              EventHelper.triggerAddUsersFromGSuiteButtonClicked();
            }
          },
          component: GoogleIcon,
          disabled: this.disableGSuiteImport,
          text: !this.disableGSuiteImport
            ? gSuiteIntegrationMenuText
            : `${gSuiteIntegrationMenuText} (not set up)`,
        },
        {
          clickHandler: () => {
            if (this.numberOfOffice365s > 1) {
              Util.redirect(this.directoryTypeRoute(DirectoryTypeConsts.office365));
            } else {
              this.navigateToDirectoryAside(office365);
              EventHelper.triggerAddUsersFromO365ButtonClicked();
            }
          },
          component: M365Icon,
          disabled: this.disableOffice365Import,
          text: !this.disableOffice365Import
            ? M365IntegrationMenuText
            : `${M365IntegrationMenuText} (not set up)`,
        },
        {
          clickHandler: '/#/directories/active-directory',
          component: ActiveDirectoryIcon,
          componentProps: iconProps,
          text: DirectoryUtil
            .getDirectoryImportMenuItemText(DirectoryNameConsts[activeDirectory].slice(0, 16)),
        },
        {
          clickHandler: 'https://support.jumpcloud.com/support/s/article/Integrating-BambooHR-with-JumpCloud',
          component: BambooIcon,
          componentProps: iconProps,
          text: DirectoryUtil.getDirectoryImportMenuItemText(bambooHR, true),
          external: true,
          hide: !this.bambooRealTimeImport,
          showExternalLinkIcon: true,
        },
        {
          clickHandler: KbArticleUrls.integratingWithHiBob,
          component: HiBobIcon,
          componentProps: iconProps,
          text: DirectoryUtil.getDirectoryImportMenuItemText(DirectoryNameConsts[bob], true),
          external: true,
          showExternalLinkIcon: true,
        },
        ...this.appsMenuItems,
        {
          clickHandler: '/#/directories/hr-directories',
          component: WorkdayIcon,
          componentProps: iconProps,
          text: DirectoryUtil
            .getDirectoryImportMenuItemText(DirectoryNameConsts[workday].slice(0, 7)),
        },
        {
          clickHandler: KbArticleUrls.oktaIntegration,
          component: OktaIcon,
          componentProps: iconProps,
          external: true,
          showExternalLinkIcon: true,
          text: DirectoryUtil.getDirectoryImportMenuItemText(DirectoryNameConsts[okta], true),
        },
      ];

      mainActionMenuItems.push({
        addSeparator: true,
        clickHandler: 'https://support.jumpcloud.com/s/article/Import-users-from-a-custom-rest-API-integration',
        component: SsoIcon,
        componentProps: iconProps,
        external: true,
        text: 'Set up a custom API integration',
      });

      mainActionMenuItems.push({
        clickHandler: 'https://support.jumpcloud.com/s/article/Importing-from-SCIM-IdM-Integration',
        component: SsoIcon,
        componentProps: iconProps,
        external: true,
        text: 'Set up a custom SCIM integration',
      });

      return mainActionMenuItems.filter(item => !item.hide);
    },

    primaryButtonProps() {
      return PrimaryButtonProps({
        isLoading: false,
        isDisabled: false,
        hasOutline: true,
        text: 'Delete User',
        type: 'error',
      });
    },

    shouldShowOutOfLicensesPrompt() {
      const systemUserCount = this.totalCountOfAllItems;
      const hasUserCapDefined = this.maxSystemUsers > 0;
      const exceedingCap = hasUserCapDefined
          && systemUserCount >= this.maxSystemUsers;

      return (this.isProviderOrg && exceedingCap);
    },

    showEmptyFilteredUserState() {
      const emptyStates = [
        ModuleConsts.systemUser.accountStates.activated,
        ModuleConsts.systemUser.accountStates.staged,
        ModuleConsts.systemUser.accountStates.suspended,
      ];

      const filters = this.getAppliedFilters;

      const supported = filters.length === 1
        && filters[0].name === 'state'
        && emptyStates.includes(filters[0].value);

      return supported && this.items.length === 0;
    },
  },

  watch: {
    async getSelectedItems(newSelectedItems) {
      const selectedItemsChanged = this.watchSelectedItems
      && newSelectedItems.length !== this.previouslySelectedItems.length;
      if (selectedItemsChanged) {
        const selectedItems = xor(newSelectedItems, this.previouslySelectedItems);
        this.$emit('selectedItemsChanged', selectedItems, 'selected');
      }
    },

    /**
     * The `modifiedItemCount` is updated when a system user is updated. When the count changes,
     * refresh the items in the current page, so that if appropriate, the new
     * or updated item will appear in the list. Note that due to sorting, pagination, or filtering,
     * the new or updated item might not appear in the list.
     */
    async modifiedItemCount() {
      await this.getDetailsForOrganization({ id: this.orgId });
      await this.refreshCollection();
    },

    async unselectedItems(newUnselectedItems) {
      const unselectedItems = intersection(newUnselectedItems,
        this.previouslySelectedItems);
      if (this.watchSelectedItems) this.$emit('selectedItemsChanged', unselectedItems, 'unselected');
    },
  },

  beforeCreate() {
    this.$options.methods.handleBeforeCreate(this.$options, systemUserCollection);
  },

  created() {
    const loadAppTemplates = async () => {
      this.appTemplates = await getApplicationTemplatesByName(
        Object.keys(APPS),
        ['_id'],
      );
    };
    loadAppTemplates();
  },

  mounted() {
    this.getDetailsForOrganization({ id: this.orgId });
    this.setDirectoryCounts();
    this.setSudoStatusFlow();
  },

  methods: {
    ...mapActions(organizationsCollection, ['getDetailsForOrganization']),

    ...mapActions(settingsModule, ['fetchSettings']),

    ...mapActions(systemUserCollection, [
      'bulkForcePasswordChange',
      'bulkUnlock',
      'deleteItems',
      'listManagerUsers',
      'refreshCollection',
      'selectItem',
      'unselectItems',
      'updateModifiedItemCount',
      'updateItems',
    ]),

    ...mapGetters(systemUserCollection, [
      'getBoundItem',
    ]),

    ...mapActions(directoryCollection, {
      fetchDirectories: 'fetchCollection',
    }),

    // If mfa is not configured, remove the mfa status column
    adjustColumnsForMfa(columnsToDisplay) {
      const newMfaColumns = [
        SystemUserItemTableColumns.mfaOverallStatus,
        SystemUserItemTableColumns.mfaPushStatus,
        SystemUserItemTableColumns.mfaTotpStatus,
        SystemUserItemTableColumns.mfaUserRequirementStatus,
        SystemUserItemTableColumns.mfaWebAuthnStatus,
      ];
      if (!this.showMfaStatus) {
        const nameOfColumnsToRemove = map(newMfaColumns, 'name');
        columnsToDisplay.filter(
          ({ name }) => !nameOfColumnsToRemove.includes(name),
        );
      }
    },

    closeModal(modalType) {
      this.systemUserStateChange[modalType] = false;
    },

    deleteSelectedUsers() {
      this.showDeleteUserModal = false;
      this.managers = [];
      this.managerText = '';

      if (this.selectedItems.length) {
        const self = this;
        rbacUtil.dontUseNotifyUtil(() => {
          this.show403error = true;
        });
        this
          .deleteItems(this.selectedItems)
          .then((result) => {
            const { deletedIds, failedTasks } = result;

            self.deletedUsers.deletedIds.push(...deletedIds);
            self.deletedUsers.successMessage = `${pluralize('user', deletedIds.length, true)} ${pluralize('was', deletedIds.length)} removed from JumpCloud.`;

            self.deletedUsers.failedTasks.push(...failedTasks);
            self.deletedUsers.errorMessage = `${pluralize('user', failedTasks.length, true)} couldn't be removed from JumpCloud.`;
            self.updateModifiedItemCount();
          })
          .catch(() => {});
      }
    },

    displayUnableToRemoveActiveDirectoryUser() {
      const componentProps = {
        headerText: 'Unable to Remove Active Directory User(s)',
        bodyText: 'Users that are bound to an Active Directory Domain must be removed from the domain on the Active Directory Server.',
        cancelText: '',
        continueText: 'OK',
        handleCancel: () => ModalEventBus.close(),
        handleContinue: () => ModalEventBus.close(),
        showClose: true,
      };

      ModalEventBus.open({
        isLargeModal: false,
        component: 'ConfirmationModal',
        componentProps,
      });

      return new Promise(() => {});
    },

    displayUnableToRemoveSambaServiceUserModal() {
      const sambaServiceUser = this.getSambaServiceUser();

      const componentProps = {
        headerText: 'Unable to Remove User',
        bodyText: `${this.getDisplayName(sambaServiceUser)} is the active Samba Service Account. To remove them from LDAP, you must designate another Samba Service Account or disable Samba authentication in the JumpCloud LDAP Configuration.`,
        cancelText: '',
        continueText: 'OK',
        handleCancel: () => ModalEventBus.close(),
        handleContinue: () => ModalEventBus.close(),
        showClose: true,
      };

      ModalEventBus.open({
        isLargeModal: false,
        component: 'ConfirmationModal',
        componentProps,
      });

      return new Promise(() => {});
    },

    getColumnsData(columnType) {
      let columnsToDisplay = [];

      if (this.parentType === ItemTypeConsts.ldap) {
        columnsToDisplay = this.ldapSystemUserDefaultColumns;
      } else if (this.parentType === ItemTypeConsts.device) {
        columnsToDisplay = this.devicesSystemUserDefaultColumns;
      } else if (this.columns.length > 0) {
        columnsToDisplay = this[columnType];
        // This logic will need to persist even after removing the mfa columns feature flag
        columnsToDisplay = this.replaceArchivedMfaColumns(columnsToDisplay);
      } else {
        columnsToDisplay = this.systemUserDefaultColumns;
      }

      if (this.getAppliedFilters.some(({ name }) => name === 'sudo')) {
        columnsToDisplay = columnsToDisplay.concat(this.systemUserSudoColumns);
      } else if (this.getAppliedFilters.some(({ name }) => name === 'passwordless_sudo')) {
        columnsToDisplay = columnsToDisplay.concat(this.systemUserPasswordlessSudoColumns);
      }

      return uniqBy(columnsToDisplay, ({ name }) => name);
    },

    getSambaServiceUser() {
      return this.items.filter(item => item.samba_service_user)[0];
    },

    handle403AlertHidden() {
      this.show403error = false;
    },

    /* ******************************** ACTIVATE A USER ******************************** */
    async handleActivateUser() {
      this.closeAsidePanel();

      if (this.selectedItems.length) {
        this.$refs.itemTable.setLoadingOverlay(true);

        // Fetch the scheduled events so that the informational banner has the correct count
        await this.$store.dispatch(`${systemUserCollection}/fetchNextScheduledEvent`, this.selectedItems);
        this.systemUserStateChange.showBulkActivateUserModal = true;

        this.$refs.itemTable.setLoadingOverlay(false);
      }
    },

    /* ***************************** RESET USER PASSWORD ***************************** */
    /**
     * Sends the selected users a "Reset Password" email
     */
    handleBulkAction() {
      // Make sure we have at least one user selected
      if (!this.selectedItems.length) {
        notify(error, selectedItemsEmpty);
        return;
      }

      this.sendSelectedUsersEmails();
    },

    /* ******************************** DELETE A USER ******************************** */
    /**
     * Deletes selected users. If there are undeletable users, this will prompt
     * the user with an action to be able to unselect these users from the current selection then
     * execute the delete action.
     */
    handleBulkDelete() {
      this.closeAsidePanel();
      this.showDeleteModal();
    },

    /**
     * Disables MFA for the selected users. If there are users that cannot
     * have MFA disabled, these users will be unselected. Displays a modal to
     * prompt the user to continue.
     */
    handleBulkDisableMFA() {
      this.closeAsidePanel();
      this.mfaModal.disableMFAModalVisible = true;
    },

    /* ******************************* MFA BULK ACTIONS ******************************* */
    /**
     * Enables MFA for the selected users. If there are users that cannot
     * have MFA enabled, these users will be unselected. Displays a modal to
     * prompt the user to continue.
     */
    handleBulkEnableMFA() {
      this.closeAsidePanel();
      this.mfaModal.enableMFAModalVisible = true;
    },

    /* ********************** FORCE PASSWORD CHANGE BULK ACTION ********************** */
    /**
     * Expires selected users to force a password change on next login. This
     * only applies to users that are not expired.
     */
    handleBulkForcePasswordChange() {
      const { enableDaysAfterExpirationToSelfRecover } = this.passwordPolicy;
      let bodyText;
      let cancelText;
      let continueText;
      let infoText;
      let mainActionAttention;

      this.closeAsidePanel();

      const numUsers = this.selectedItems.length;
      const inEligibleCount = this.usersIneligibleForForcePasswordChange.length;
      const eligibleCount = numUsers - inEligibleCount;

      // No selected users are eligible for force password change; take no action in this case
      if (inEligibleCount === numUsers) {
        bodyText = '**None** of the selected users are eligible for forcing a password change.';
        cancelText = '';
        continueText = 'ok';
        mainActionAttention = false;
      } else {
        bodyText = `**${eligibleCount} of ${numUsers}** selected users can be forced to change their passwords.`;

        infoText = !enableDaysAfterExpirationToSelfRecover ? systemUserMessageText.forcePasswordInfoText : '';
      }

      const componentProps = {
        bodyText,
        cancelText,
        continueText,
        infoText,
        mainActionAttention,
        plural: true,

        handleContinue: () => {
          ModalEventBus.close();

          if (inEligibleCount !== numUsers) {
            // Deselect users that are ineligible
            if (inEligibleCount) {
              this.unselectItems({
                ids: this.usersIneligibleForForcePasswordChange.map(item => item.id),
              });
            }

            return this.policyChange()
              .then(policy => this.updateOrgPolicy(policy))
              .then(() => this.bulkForcePasswordChange({ ids: this.selectedItems }))
              .then((response) => {
                // Notify user of success or error
                let notifyType = error;
                let notifyMessage = `Error updating ${pluralize('user', numUsers)}.`;
                if (get(response, 'numSuccess')) {
                  notifyType = success;
                  notifyMessage = (numUsers === 1)
                    ? '1 user was successfully updated.'
                    : `${response.numSuccess} of ${numUsers} users were successfully updated.`;
                  this.unselectItems({ ids: this.selectedItems });
                }
                return notify(notifyType, notifyMessage);
              }, err => notify(error, get(err, 'message', 'Unable to update organization.')));
          }
          return undefined;
        },
      };

      ModalEventBus.open({
        component: 'ForcePasswordChangeModal',
        componentProps,
        preventCloseOnOverlayClick: true,
      });
    },

    /**
     * Resets MFA for the selected users. If there are users that cannot
     * have MFA reset, these users will be unselected. Displays a modal to
     * prompt the user to continue.
     */
    handleBulkResetMFA() {
      this.closeAsidePanel();
      this.mfaModal.resetMFAModalVisible = true;
    },

    /* ******************************* UNLOCK BULK ACTION ******************************* */
    /**
     * Unlocks selected users. This only applies to users that are locked.
     */
    handleBulkUnlock() {
      this.closeAsidePanel();
      ModalEventBus.open({
        component: UnlockUserModal,
        componentProps: {
          action: () => {
            ModalEventBus.close();

            // No selected users are eligible for unlock; take no action
            const numUsers = this.selectedItems.length;
            if (this.usersIneligibleForUnlock.length === numUsers) {
              return;
            }

            // Unselect users ineligible for unlock
            if (this.usersIneligibleForUnlock.length) {
              this.unselectItems({ ids: this.usersIneligibleForUnlock.map(item => item.id) });
            }

            this.bulkUnlock({ ids: this.selectedItems })
              .then((response) => {
                // Notify user of success or error
                let notifyType = error;
                let notifyMessage = `Error updating ${pluralize('user', numUsers)}.`;
                if (get(response, 'numSuccess')) {
                  notifyType = success;
                  notifyMessage = (numUsers === 1)
                    ? '1 user was successfully updated.'
                    : `${response.numSuccess} of ${numUsers} users were successfully updated.`;
                  this.unselectItems({ ids: this.selectedItems });
                }
                notify(notifyType, notifyMessage);
              });
          },
        },
      });
    },

    handleCancelDeleteUserModal() {
      this.showDeleteUserModal = false;
      this.managers = [];
      this.managerText = '';
    },

    /**
     * Reset the particular section of the deletedUsers data member to an empty array when then
     * Alert is hidden.
     */
    handleDeleteAlertHidden(key) {
      this.deletedUsers[key].splice(0);
    },

    /* ********************************* ADD A USER ********************************* */
    /**
     * Action for when the admin clicks the "plus" icon
     * Opens the aside panel to allow the admin to add a user
     */
    handleMainAction() {
      EventHelper.triggerAddNewUserButtonClicked();

      // MSP-962, we only want to care about the following if the organization
      // a direct organization, not a tenant organization (provider org).
      const systemUserCount = this.totalCountOfAllItems;
      if (!this.isProviderOrg && (systemUserCount >= this.maxSystemUsers && !this.isCustomer)) {
        if (this.hasContract && this.hasBilling) {
          PaymentHelper.openPaymentInformationForClosedCustomer(paymentSources.manualUserEntry);
        } else if (this.hasBilling) {
          this.shouldShowPaywall = true;
        } else {
          ModalEventBus.open({
            component: 'ConfirmationModal',
            componentProps: {
              headerText: 'Missing payment method',
              // eslint-disable-next-line max-len
              mdTemplate: 'Please add or update a payment method on your account in order to add more users. Administrators with billing access can set this up by clicking the menu below their username and selecting "Payments".\n\nIf you encounter any additional problems, contact JumpCloud Support at [support@jumpcloud.com](mailto:support@jumpcloud.com).',
              cancelText: '',
              continueText: 'okay',
            },
          });
        }
        return;
      }

      // MSP-513, if we're over/at the limit and belong to a provider...
      // License prompt will be considered (in its logic) if we're a provider
      if (this.shouldShowOutOfLicensesPrompt) {
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            headerText: 'No Available Licenses',
            bodyText: 'There are no more available licenses for this organization. Please contact your service provider to add more.',
            cancelText: '',
            continueText: 'okay',
          },
        });
        return;
      }

      RouterService.redirect({
        path: `${this.route}/add`,
        viewport: this.viewport,
        vueRouter: this.$router,
      });
    },

    /* ******************************** SUSPEND A USER ******************************** */
    async handleSuspendUser() {
      this.closeAsidePanel();

      if (this.selectedItems.length) {
        this.$refs.itemTable.setLoadingOverlay(true);

        // Fetch the scheduled events so that the informational banner has the correct count
        await this.$store.dispatch(`${systemUserCollection}/fetchNextScheduledEvent`, this.selectedItems);
        this.systemUserStateChange.showSuspendModal = true;

        this.$refs.itemTable.setLoadingOverlay(false);
      }
    },

    handleUnselectLockedSystemUser(id) {
      if (this.inLdapDirectoryAside() && this.isSambaServiceInListOfIds([id])) {
        return this.displayUnableToRemoveSambaServiceUserModal();
      }

      if (this.inActiveDirectoryAside() && this.isItemPreviouslySelected(id)) {
        return this.displayUnableToRemoveActiveDirectoryUser();
      }

      return this.handleUnselectLockedItem(id);
    },

    handleUnselectMultipleLockedSystemUsers(ids) {
      if (this.inLdapDirectoryAside() && this.isSambaServiceInListOfIds(ids)) {
        this.$refs.itemTable.setLoadingOverlay(false);
        return this.displayUnableToRemoveSambaServiceUserModal();
      }

      if (this.inActiveDirectoryAside() && ids.some(id => this.isItemPreviouslySelected(id))) {
        this.$refs.itemTable.setLoadingOverlay(false);
        return this.displayUnableToRemoveActiveDirectoryUser();
      }

      return this.handleUnselectMultipleLockedItems(ids);
    },

    inActiveDirectoryAside() {
      return this.panelType === ItemTableConsts.panelType.adDomainAsidePanel;
    },

    inSystemAside() {
      return this.panelType === ItemTableConsts.panelType.systemAsidePanel;
    },

    isSambaServiceInListOfIds(ids) {
      return this.items
        .filter(item => ids.indexOf(item.id) !== -1 && item.samba_service_user)
        .length === 1;
    },

    isSystemUserLocked(id) {
      return (this.inLdapDirectoryAside() && this.isSambaServiceInListOfIds([id]))
        || (this.inActiveDirectoryAside() && this.isItemPreviouslySelected(id))
        || this.isItemLocked(id);
    },

    navigateToDirectoryAside(directoryType) {
      const newDirectoryUrl = `${this.directoryTypeRoute(directoryType)}/${directoryType}/new`;

      // Check what directory instances the admin already has
      // NOTE: May want to consider moving this fetch call to when the admin first opens
      // the main action button dropdown menu
      DirectoryApi.fetchAll()
        .then((resp) => {
          // If admin already has a directory instance, navigate to first instance
          const firstInstance = resp.find(d => d.type === directoryType);
          if (firstInstance) {
            Util.redirect(`${this.directoryTypeRoute(directoryType)}/${directoryType}/${firstInstance.id}/details`);
            return;
          }

          // Otherwise, navigate to aside panel to create an instance
          Util.redirect(newDirectoryUrl);
        });
    },

    // Determine if the org policy needs to be updated; async so that it returns a promise
    async policyChange() {
      const policy = clone(this.passwordPolicy);
      if (policy.enableDaysAfterExpirationToSelfRecover) {
        return undefined;
      }
      policy.enableDaysAfterExpirationToSelfRecover = true;
      return policy;
    },

    sendSelectedUsersEmailContinue(recipientEmail) {
      SystemUserApi
        .sendEmail(this.selectedItems, recipientEmail)
        .then((users) => {
          if (users && users.length) {
            const usernames = util.formatListAsSeries(map(users, 'username'));
            notify(
              'info',
              `Email resent to ${pluralize('user', users.length, true)}: ${usernames}.`,
            );
            this.unselectItems({ ids: this.selectedItems });
          } else {
            notify(error, failedActivationEmailResend);
          }
        })
        .finally(() => ModalEventBus.close());
    },

    /**
     * Creates a confirmation modal for the resend email bulk action.
     */
    sendSelectedUsersEmails() {
      this.selectedActiveUserDetails = this.selectedItems
        .map(id => this.boundItemsById[id])
        .filter(user => user && user.state === ModuleConsts.systemUser.accountStates.activated);

      if (this.selectedActiveUserDetails.length === 0) {
        this.showNonEligibleSelectedUserModal();
        return;
      }

      const isAtLeastOneSystemUserNotActivated = this.selectedActiveUserDetails
        .some(({ activated }) => !activated);

      if (isAtLeastOneSystemUserNotActivated) {
        this.showBulkResendActivationEmailModal = true;
      } else {
        this.showBulkResendPasswordResetEmail = true;
      }
    },

    /**
     * Fetch the number of Google and Microsoft 365 directories so that the
     * Import item text can be enabled/disabled in the main action menu.
     */
    setDirectoryCounts() {
      const filters = [{
        name: 'type',
        operator: 'eq',
        value: `${gSuite}|${office365}`,
      }];

      DirectoryApi
        .fetch(0, 100, undefined, undefined, undefined, filters)
        .then((directories) => {
          this.numberOfGSuites = directories
            .results
            .filter(dir => dir.type === gSuite).length;
          this.numberOfOffice365s = directories
            .results
            .filter(dir => dir.type === office365).length;
        });
    },

    setManagerText() {
      if (this.managers.length === 1) {
        const userName = this.managers[0].name.split(',').reverse().join('');
        this.managerText = `${userName} is a manager. Deleting a manager may affect any JumpCloud-managed applications that expect a user with a manager role.`;
      }

      if (this.managers.length === 1 && this.selectedItems.length > 1) {
        this.severityModalData.subtitle = 'Are you sure you want to delete the selected users?';
      }

      if (this.managers.length === 1 && this.selectedItems.length === 1) {
        this.severityModalData.subtitle = 'Are you sure you want to delete this user?';
      }

      if (this.managers.length > 1) {
        this.severityModalData.subtitle = 'Are you sure you want to delete the selected users?';
        let managersString = '';
        this.managers.forEach(obj => {
          managersString = managersString.concat(`${obj.name}, \n`);
        });
        managersString = managersString.substring(0, managersString.length - 3);

        this.managerText = `Some of the selected users are managers. 
        
        Deleting a manager may affect any JumpCloud-managed applications that expect a user with a manager role. The following users are managers: 
        
        **${managersString}**
        `;
      }
    },

    setSudoStatusFlow() {
      SystemUserItemTableColumns.sudoStatus.bodyComponentProps
        .showNewWorkflow = this.showNewSudoWorkflow;
    },

    showDeleteFailureDetails() {
      const unremovableUsers = this.deletedUsers.failedTasks.map(failedTask => ({
        user: this.getBoundItem()(failedTask.id),
        error: failedTask.error,
      }));

      this.handleDeleteAlertHidden('failedTasks');

      ModalEventBus.open({
        component: UnremovableUsersModal,
        componentProps: { unremovableUsers },
        isLargeModal: true,
      });
    },

    async showDeleteModal() {
      const selectedCount = this.selectedItems.length;

      if (!selectedCount) {
        return;
      }

      this.managers = await this.listManagerUsers({ ids: this.selectedItems });

      if (!isEmpty(this.managers)) {
        this.setManagerText();
      } else {
        this.severityModalData.subtitle = 'Are you sure you want to delete this user?';
      }

      this.severityModalData.kbText = 'Managing User States.';
      this.severityModalData.kbLink = KbArticleUrls.managingUserStates;
      this.severityModalData.notificationTitle = 'User Deletion';
      this.severityModalData.numConfirmText = 'Confirm the number of users to be deleted.';
      this.severityModalData.title = 'Delete User';

      this.showDeleteUserModal = true;
    },

    /* ******************************* Import CSV Actions ***************************** */
    showImportFromCSVModal() {
      EventHelper.triggerAddNewUserFromCSVButtonClicked();
      ImportFromCSVModalHelper.showImportFromCSVModal();
    },

    showNonEligibleSelectedUserModal() {
      const componentProps = {
        mdTemplate: '**None** of the selected users are eligible to receive emails.',
        cancelText: '',
        headerText: 'Send Users Emails',
        continueText: 'ok',
        mainActionAttention: false,
        handleContinue: () => {
          ModalEventBus.close();
        },
      };

      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps,
        preventCloseOnOverlayClick: true,
      });
    },

    suspendSelectedUsers() {
      const {
        sambaServiceUsers,
        suspendedUsers,
      } = this.usersIneligibleForSuspension || {};
      const unsuspendableUsers = sambaServiceUsers.concat(suspendedUsers);

      this.showSuspendModal = false;

      this.unselectItems({ ids: unsuspendableUsers.map(item => item.id) });

      if (this.selectedItems.length) {
        const currentSelectedItems = clone(this.selectedItems);
        this.updateItems({
          ids: currentSelectedItems,
          attributes: {
            state: ModuleConsts.systemUser.accountStates.suspended,
            suspended: true, // Will be deprecated in the future
          },
        }).then((response) => {
          const notificationType = response.numSuccess > 0 ? success : error;
          notify(notificationType, `${response.numSuccess} of ${pluralize('user', currentSelectedItems.length, true)} ${pluralize('has', currentSelectedItems.length)} been successfully suspended.`);
          this.unselectItems({ ids: this.selectedItems });
          this.updateModifiedItemCount();
        }).catch(() => {
          notify(error, usersCouldNotBeSuspended);
        });
      }
    },

    // Update the org policy that allows users to have their passwords forcibly changed
    // and refresh settings vuex collection
    updateOrgPolicy(policy) {
      if (policy !== undefined) {
        return OrganizationsApi
          .updateOrganizationSettings(this.orgId, { settings: { passwordPolicy: policy } })
          .then(() => this.fetchSettings());
      }
      return undefined;
    },
  },
};
</script>

<style module>
.containsAlerts {
  position: relative;
}

.deleteAlert {
  width: 320px;
}

.showDeleteFailureDetailsLink {
  display: block;
}

.itemTable th:nth-child(3),
.itemTable td:nth-child(3) {
  max-width: 40ch;
}
</style>
